import * as preact from "preact";

import readlangClient, { requestErrorMessage } from "readlangClient";
import { isValidUsername } from "shared/usernameValidation";
import stores from "stores/stores";
import type { UserAttrs } from "stores/UserStore";

interface EditProfileProps {
  finished?: () => void;
}

interface EditProfileState {
  email: string;
  currentPassword: string;
  emailError: string;
  passwordLinkSent: boolean;
  displayName: string;
  username: string;
  homepageURL: string;
  location: string;
  about: string;
  publicDirty: boolean;
}

type EditableProfileField =
  | "email"
  | "displayName"
  | "username"
  | "homepageURL"
  | "location";

const stateFromUser = (): EditProfileState => {
  const user = stores.user.state.value;
  return {
    email: user.email ?? "",
    currentPassword: "",
    emailError: "",
    passwordLinkSent: false,
    displayName: user.displayName ?? "",
    username: user.username ?? "",
    homepageURL: user.homepageURL ?? "",
    location: user.location ?? "",
    about: user.about ?? "",
    publicDirty: false,
  };
};

export class EditProfile extends preact.Component<
  EditProfileProps,
  EditProfileState
> {
  private disposeUserChange?: () => void;

  state = stateFromUser();

  componentDidMount() {
    this.disposeUserChange = stores.user.onChange(
      (user) => [
        user.email,
        user.displayName,
        user.username,
        user.homepageURL,
        user.location,
        user.about,
      ],
      () => {
        this.setState(stateFromUser());
      }
    );
  }

  componentWillUnmount() {
    this.disposeUserChange?.();
  }

  private emailChanged() {
    const normalize = (email = "") => email.trim().toLowerCase();
    return (
      normalize(this.state.email) !== normalize(stores.user.state.value.email)
    );
  }

  updateField = (field: EditableProfileField) => (event: Event) => {
    const value = (event.currentTarget as HTMLInputElement).value;
    this.setState({
      [field]: value,
      publicDirty: field === "email" ? this.state.publicDirty : true,
    } as Pick<EditProfileState, typeof field> & Partial<EditProfileState>);
  };

  updateAbout = (event: Event) => {
    this.setState({
      about: (event.currentTarget as HTMLTextAreaElement).value,
      publicDirty: true,
    });
  };

  updateCurrentPassword = (event: Event) => {
    this.setState({
      currentPassword: (event.currentTarget as HTMLInputElement).value,
    });
  };

  /**
   * The address only changes once the link mailed to it is opened, so until
   * then the form goes back to showing the current one, with a note.
   */
  requestEmailChange = async () => {
    try {
      const { data } = await readlangClient.request<{ pendingEmail: string }>(
        "PATCH",
        "/api/user",
        { email: this.state.email, currentPassword: this.state.currentPassword }
      );
      stores.user.patch({ pendingEmail: data.pendingEmail });
      this.setState({
        email: stores.user.state.value.email ?? "",
        emailError: "",
      });
    } catch (error) {
      this.setState({
        emailError: error.jqXHR?.alreadyNotified
          ? ""
          : requestErrorMessage(error),
      });
    } finally {
      this.setState({ currentPassword: "" });
    }
  };

  /** An email change needs a password, which Google sign-ups set this way. */
  sendSetPasswordLink = async (event: Event) => {
    event.preventDefault();
    await readlangClient.request("POST", "/api/forgotPassword", {
      email: stores.user.state.value.email,
    });
    this.setState({ passwordLinkSent: true });
  };

  savePublic = () => {
    const username = this.state.username.trim().toLowerCase();

    if (!isValidUsername(username)) {
      alert(
        "Invalid username. Use 2-30 lowercase letters, numbers, and hyphens only."
      );
      return;
    }

    const newUser: UserAttrs = {
      displayName: this.state.displayName,
      username,
      homepageURL: this.state.homepageURL,
      location: this.state.location,
      about: this.state.about,
    };

    stores.user
      .save(newUser)
      .then(() => {
        this.props.finished?.();
      })
      .catch(() => {
        alert("That username already exists, please choose a different one");
      });
  };

  render() {
    const user = stores.user.state.value;
    const emailChanged = this.emailChanged();

    return (
      <>
        <h2>Edit profile</h2>
        <div id="accountForm">
          <table>
            <tbody>
              <tr>
                <td colSpan={2}>
                  <h3 class="spacing">Private information</h3>
                </td>
              </tr>
              <tr>
                <td>Email</td>
                <td>
                  <input
                    disabled={!user.hasPassword}
                    name="email"
                    onInput={this.updateField("email")}
                    type="text"
                    value={this.state.email}
                  />
                </td>
              </tr>
              {user.pendingEmail && (
                <tr class="fieldHint">
                  <td></td>
                  <td>
                    To finish changing your email address, open the link we sent
                    to {user.pendingEmail}.
                  </td>
                </tr>
              )}
              {emailChanged && user.hasPassword && (
                <tr>
                  <td>Current Password</td>
                  <td>
                    <input
                      name="currentPassword"
                      onInput={this.updateCurrentPassword}
                      type="password"
                      value={this.state.currentPassword}
                    />
                  </td>
                </tr>
              )}
              {!user.hasPassword && (
                <tr class="fieldHint">
                  <td></td>
                  <td>
                    {this.state.passwordLinkSent ? (
                      <>Check your inbox for a link to set a password.</>
                    ) : (
                      <>
                        To change your email address, first{" "}
                        <a href="#" onClick={this.sendSetPasswordLink}>
                          set a password
                        </a>
                        .
                      </>
                    )}
                  </td>
                </tr>
              )}
              {this.state.emailError && (
                <tr>
                  <td></td>
                  <td class="emailChangeError">{this.state.emailError}</td>
                </tr>
              )}
              {user.hasPassword && (
                <tr>
                  <td></td>
                  <td class="buttonContainer">
                    <button
                      class="submitEmail prominent"
                      disabled={!emailChanged || !this.state.currentPassword}
                      onClick={this.requestEmailChange}
                    >
                      Save Email
                    </button>
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={2}>
                  <h3 class="spacing">Public information</h3>
                </td>
              </tr>
              <tr>
                <td>Display Name</td>
                <td>
                  <input
                    name="displayName"
                    onInput={this.updateField("displayName")}
                    type="text"
                    value={this.state.displayName}
                  />
                </td>
              </tr>
              <tr>
                <td>Username</td>
                <td>
                  <input
                    name="username"
                    onInput={this.updateField("username")}
                    pattern="[a-z0-9][a-z0-9\-]*[a-z0-9]"
                    type="text"
                    value={this.state.username}
                  />
                </td>
              </tr>
              <tr class="fieldHint">
                <td></td>
                <td>Lowercase letters, numbers, and hyphens. Used in URLs.</td>
              </tr>
              <tr>
                <td>Homepage URL</td>
                <td>
                  <input
                    name="homepageURL"
                    onInput={this.updateField("homepageURL")}
                    type="url"
                    value={this.state.homepageURL}
                  />
                </td>
              </tr>
              <tr>
                <td>Location</td>
                <td>
                  <input
                    name="location"
                    onInput={this.updateField("location")}
                    type="text"
                    value={this.state.location}
                  />
                </td>
              </tr>
              <tr>
                <td>About</td>
                <td>
                  <textarea
                    name="about"
                    onInput={this.updateAbout}
                    rows={5}
                    value={this.state.about}
                  />
                </td>
              </tr>
              <tr>
                <td></td>
                <td class="buttonContainer">
                  <button
                    class="submitPublic prominent"
                    disabled={!this.state.publicDirty}
                    onClick={this.savePublic}
                  >
                    Save Public information
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          {user.googlePlusInfo?.picture ? (
            <>Profile image comes from your Google account.</>
          ) : (
            <>
              <a href="http://gravatar.com">
                Set your profile photo with Gravatar
              </a>
              .
              <br />
            </>
          )}
        </p>
      </>
    );
  }
}
